<template>
  <ReservationPanel sticky @search="getAccommodationData" ref="reservationPanel" :key="$route.fullPath" />

  <div class="container--Medium">
    <div class="accommodationList__Intro">
      <h1 v-text="page.title" />
      <client-only>
        <h2 v-text="pageTitle" />
      </client-only>
      <div>
        <CMSField v-if="props.page?.content" :data="props.page.content" />
      </div>
    </div>

    <div class="accommodationList__FiltersWrapper">
      <Filters :data="filtersList" v-model="selectedFilters" @item-remove="updateResults" @route-change="updateResults" />
      <div v-if="!responseError" class="accommodationList__FiltersTop">
        <p>{{ $t('propertiesAvailable', accommodationsListing.length + unavailableAccommodationsListing.length) }}</p>
      </div>
    </div>

    <!-- <div v-if="responseError" class="apiFetchError">
      <p v-text="responseError" />
    </div> -->

    <section class="apiFetchError apiFetchError--Success" v-if="promoCodeApplied">
      <div class="apiFetchError__Wrapper">
        <div class="apiFetchError__Text">
          <img src="/resources/images/icons/discount.svg" width="24" height="24"/>
          <p v-text="$t('accommodationUnits.promoCodeApplied')"></p>
        </div>
        <div class="apiFetchError__Button">
          <button class="button__Text button__Text--Underline" @click="editPromoCode">{{ $t('fetchErrors.remove') }}</button>
        </div>
      </div>
    </section>

    <section v-if="responseError" class="apiFetchError">
      <div class="apiFetchError__Wrapper">
        <div class="apiFetchError__Text">
          <img src="/resources/images/icons/discount.svg" width="24" height="24" v-if="responseError === 'invalid_promo_code'"/>
          <img src="/resources/images/icons/warning.svg" width="24" height="24" v-else/>
          <p v-text="$t(`fetchErrors.${ responseError }`)"></p>
        </div>
        <div class="apiFetchError__Button" v-if="responseError === 'invalid_promo_code'">
          <button class="button__Text button__Text--Underline" @click="editPromoCode">{{ $t('fetchErrors.remove') }}</button>
        </div>
      </div>
    </section>
    <template v-else>
      <div v-if="accommodationsListing.length" class="accommodationList__ListWrapper">
        <PropertyItem v-for="(item, index) in accommodationsListing" :property="item" :key="item.id" :short-name="true" :ecommerceItem="ecommerceItems[index]" />
      </div>
  
      <div v-if="unavailableAccommodationsListing.length" class="accommodationList__ListWrapper">
        <h2 v-text="$t('notAvailable')" />
        <PropertyItem v-for="item in unavailableAccommodationsListing" :property="item" not-available :key="item.id" :short-name="true" />
      </div>
    </template>
  </div>

  <Loader :active="fetchingData" />
</template>

<script setup>
  import { format } from 'date-fns';
  import { FilterIdentifiersObject } from '~/utils/types';
  const { t } = useI18n();
  const { booking } = useBooking();
  const { page } = usePage();
  const { initialData, accommodationsData } = useSetup();
  const selectedFilters = ref({});
  const accommodationsListing = ref([]);
  const unavailableAccommodationsListing = ref([]);
  const originalListing = ref([]);
  const originalUnavailableListing = ref([]);
  const fetchingData = ref(true);
  const responseError = ref(null);
  const reservationPanel = ref(null);
  const promoCodeApplied = ref(false);
  const dataLayerList = useState('dataLayerList');

  // get all amenities identifiers from types.ts
  const amenitiesIdentificators = [FilterIdentifiersObject.PARKING.backendId, FilterIdentifiersObject.MEAL_PLAN.backendId, FilterIdentifiersObject.HOLIDAY_TYPE.backendId];

  // set filters listing - backendId to frontendId
  const FILTERS_MAPPER = {
    [FilterIdentifiersObject.PROPERTY_TYPE.backendId]: FilterIdentifiersObject.PROPERTY_TYPE.frontendId,
    [FilterIdentifiersObject.MEAL_PLAN.backendId]: FilterIdentifiersObject.MEAL_PLAN.frontendId,
    [FilterIdentifiersObject.PARKING.backendId]: FilterIdentifiersObject.PARKING.frontendId,
    [FilterIdentifiersObject.CATEGORY_RATING.backendId]: FilterIdentifiersObject.CATEGORY_RATING.frontendId,
    [FilterIdentifiersObject.HOLIDAY_TYPE.backendId]: FilterIdentifiersObject.HOLIDAY_TYPE.frontendId,
    [FilterIdentifiersObject.BRAND.backendId]: FilterIdentifiersObject.BRAND.frontendId,
  }

  const props = defineProps({
    page: {},
    place: {
      type: String || Number,
    },
    separatePropertyTypeFilters: {
      type: Boolean,
      default: false
    }
  })

  const pageTitle = computed(() => props.page.subtitle);

  async function getAccommodationData(client = false) {
    fetchingData.value = true;
    responseError.value = null;
    let requestBody = { ...booking.value };
    if (requestBody.date_from) requestBody.date_from = format(new Date(requestBody.date_from), 'yyyy-MM-dd');
    if (requestBody.date_to) requestBody.date_to = format(new Date(requestBody.date_to), 'yyyy-MM-dd');
    if (props.page?.place_detail?.id) requestBody.place = props.page.place_detail.id;

    if (requestBody?.children_age?.length) {
      requestBody.children_age = requestBody.children_age.filter(item => item !== null);
      requestBody.children = requestBody.children_age.length;
      booking.value.children_age = JSON.parse(JSON.stringify(requestBody.children_age))
      booking.value.children = requestBody.children;
    }

    if (page.value?.parent_accommodation_kind_group) requestBody.accommodation_group = page.value.parent_accommodation_kind_group;
    else if (page.value?.accommodation_group?.id) requestBody.accommodation_group = page.value.accommodation_group.id;
    else delete requestBody.accommodation_group;

    try {
      let response = null;
      if(client) {
        response = await useClientFetch('search/accommodation/', {
          method: 'POST',
          body: requestBody
        })
      }
      else {
        response = await useServerFetch('search/accommodation/', {
          method: 'POST',
          body: requestBody
        })
      }

      if (requestBody.promo_code) {
        if (response.accommodations.length) {
          promoCodeApplied.value = true;
        }
      }
      else {
        promoCodeApplied.value = false;
      }
  
      if (response?.accommodations) {
        // save original data in originalListing
        originalListing.value = response.accommodations.map(item => ({ ...accommodationsData.value[item.accommodation], priceForPeriod: item.price || null }));
        accommodationsListing.value = Array.from(originalListing.value);
      }

      if (response?.unavailable_accommodations) {
        // save original data in originalUnavailableListing
        originalUnavailableListing.value = response.unavailable_accommodations.map(item => accommodationsData.value[item.accommodation]);
        unavailableAccommodationsListing.value = Array.from(originalUnavailableListing.value);
        
        let categoriesFilter = filtersList.value.find(item => item.identifier === FilterIdentifiersObject.CATEGORY_RATING.backendId);
        if (categoriesFilter) categoriesFilter.items = ratingFilters.value;
      }
    } catch(e) {
      console.error(e);
      if (e.data?.details?.code === 'no_results') {
        responseError.value = t('fetchErrors.accommodationsNoResults');
      }
    }
    fetchingData.value = false;
  }

  // collect all rating from accommodations and set them in filters so we can get stars ratings
  const ratingFilters = computed(() => {
    let allObjects = [...accommodationsListing.value, ...unavailableAccommodationsListing.value];
    return allObjects.reduce((acc, nextItem) => {
      if (nextItem.category_rating === 0) return acc;
      const ratingExistsInAcc = acc.find(item => item.id === nextItem.category_rating)
      if (!ratingExistsInAcc) acc.push({id: nextItem.category_rating, name: `${nextItem.category_rating}`})
      return acc;
    }, []).sort((a, b) => a.id - b.id);
  });

  const filtersList = ref([
    {
      title: t('filtersTexts.propertyType'),
      identifier: FilterIdentifiersObject.PROPERTY_TYPE.backendId,
      items: getPropertyTypeFilters(t)
    },
    {
      title: t('filtersTexts.mealPlans'),
      identifier: FilterIdentifiersObject.MEAL_PLAN.backendId,
      items: initialData.value.amenities?.[FilterIdentifiersObject.MEAL_PLAN.frontendId] ? initialData.value.amenities[FilterIdentifiersObject.MEAL_PLAN.frontendId] : []
    },
    {
      title: t('filtersTexts.parking'),
      identifier: FilterIdentifiersObject.PARKING.backendId,
      items: initialData.value.amenities?.[FilterIdentifiersObject.PARKING.frontendId] ? initialData.value.amenities[FilterIdentifiersObject.PARKING.frontendId] : []
    },
    {
      title: t('filtersTexts.categoryRating'),
      identifier: FilterIdentifiersObject.CATEGORY_RATING.backendId,
      items: ratingFilters.value
    },
    {
      title: t('filtersTexts.holidayType'),
      identifier: FilterIdentifiersObject.HOLIDAY_TYPE.backendId,
      items: initialData.value.amenities?.[FilterIdentifiersObject.HOLIDAY_TYPE.frontendId] ? initialData.value.amenities[FilterIdentifiersObject.HOLIDAY_TYPE.frontendId] : []
    },
    {
      title: t('filtersTexts.brand'),
      identifier: FilterIdentifiersObject.BRAND.backendId,
      items: initialData.value.brands
    },
  ]);

  // for all filters set initial disabled value
  for (let filterCategory of filtersList.value) {
    for (let item of filterCategory.items) {
      item.disabled = false;
    }
  }

  if (props.separatePropertyTypeFilters) {
    // on some pages we need to show only hotel or camp filters for type filter so we need to separate them
    const accommodationGroup = page.value?.accommodation_group?.id && initialData.value?.accommodation_kind_groups ? initialData.value.accommodation_kind_groups.find(group => group.id === page.value.accommodation_group.id) : '';
    const propertyTypeFilters = filtersList.value.find(filter => filter.identifier === FilterIdentifiersObject.PROPERTY_TYPE.backendId);
    if (propertyTypeFilters && accommodationGroup?.items?.length) {
      // Filter the property type items to only include those in the accommodation group
      propertyTypeFilters.items = propertyTypeFilters.items.filter(item => 
        accommodationGroup.items.includes(item.id)
      );
    }
  }

  /*await getAccommodationData();
  if (selectedFilters.value) getFilteredData();*/

  let allItems = JSON.parse(JSON.stringify(accommodationsListing.value));
  let ecommerceItems = [];
  ecommerceItems = allItems.map((accommodation, index) => {
    return {
      'item_id': accommodation.id,
      'item_name': accommodation.name,
      'item_category': accommodation.kind === 'CAMP' ? 'Campsites' : 'Hotels and resorts',
      'item_category2': accommodation.place?.name,
      'item_brand': accommodation.brand?.name,
      'item_list_name': `Destination ${accommodation.place?.name}`,
      'item_list_id': 'Search',
      'index': index + 1,
    }
  });

  onMounted(() => {
    dataLayerList.value = ecommerceItems;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      'event': 'view_item_list',
      'ecommerce': {
        'items': ecommerceItems,
      },
    });
  });

  function getFilteredData() {
    if (originalListing.value?.length) accommodationsListing.value = filterData(originalListing.value);
    if (originalUnavailableListing.value?.length) unavailableAccommodationsListing.value = filterData(originalUnavailableListing.value);
    updateFiltersStatus();
  }

  // update filters status based on current results. If there are no results for a filter, disable it
  function updateFiltersStatus() {
    const allResults = [...accommodationsListing.value, ...unavailableAccommodationsListing.value];

    for (let filterCategory of filtersList.value) {
      const filterId = filterCategory.identifier;

      const isAmenity = amenitiesIdentificators.includes(filterId);

      for (let item of filterCategory.items) {
        item.disabled = !allResults.some(accommodation => {
          const filter = isAmenity ? accommodation.amenities[FILTERS_MAPPER[filterId]] : accommodation[FILTERS_MAPPER[filterId]];
          return Array.isArray(filter) ? filter.includes(item.id) : filter === item.id;
        });
      }
    }
  }

  function filterData(data) {
    let filteredResults = JSON.parse(JSON.stringify(data));

    const filtersWithValues = JSON.parse(JSON.stringify(selectedFilters.value));
    for (let filter in filtersWithValues) {
      if (!filtersWithValues[filter].length) {
        delete filtersWithValues[filter];
      }
    }
    
    for (let accommodation of filteredResults) {
      for (let filter in filtersWithValues) {
        const accommodationIncludesFilter = filtersWithValues[filter].some(selectedFilter => {
          const filterLocation = amenitiesIdentificators.includes(filter) ? accommodation.amenities[FILTERS_MAPPER[filter]] : accommodation[FILTERS_MAPPER[filter]];
          return Array.isArray(filterLocation) ? filterLocation.includes(selectedFilter) : filterLocation === selectedFilter;
        })

        if (!accommodationIncludesFilter) {
          filteredResults = filteredResults.filter(item => item !== accommodation);
        }
      }
    }

    return filteredResults;
  }

  function updateResults() {
    setTimeout(() => {
      getFilteredData();
    }, 0);
  }

  function editPromoCode() {
    reservationPanel.value.openReservationPanel(3);
  }

  // update results on every filter change so we can immediately disable filters that have no results
  watch(() => selectedFilters.value, () => {
    setTimeout(() => {
      getFilteredData();
    }, 10);
  }, { deep: true });

  onMounted(() => {
    setTimeout(async () => {
      await getAccommodationData(true);
      if (selectedFilters.value) getFilteredData();
    }, 10);
  })
</script>

<style lang="scss">
  @import url('@/assets/styles/pages/_accommodationList.scss');
</style>